import React from "react";
import { Field, UseFieldConfig } from "react-final-form";
import FormSelect, { FormSelectCustomProps, SelectProps } from "ui/Form/SelectCustom";
import FormChipGroup from "ui/Form/ChipGroup";
import { getDeviceIcon } from "constants/devices";
import { useReferences, type getReferenceReturn } from "hooks/reference";

type Props = Omit<FormSelectCustomProps, "inputProps"> &
    UseFieldConfig<string> & {
        variant?: "select" | "chips";
    };

export interface FormReferenceProps extends Props {
    refType: keyof getReferenceReturn;
    inputProps: Omit<SelectProps, "options">;
}

const ReferenceField: React.FC<FormReferenceProps> = ({
    refType,
    labelProps,
    fieldGroupProps,
    fieldControlProps,
    adornmentStart,
    adornmentEnd,
    inputProps,
    variant = "select",
    ...fieldProps
}) => {
    const reference = useReferences();
    const options = reference.all(refType).map((subject) => {
        return {
            label: subject.value,
            value: subject.code,
            Icon: refType === "device" ? getDeviceIcon(subject.code) : undefined,
        };
    });

    return (
        <Field name={inputProps.name} {...fieldProps}>
            {({ input, meta }) => {
                const error = !!(meta.error || meta.submitError);
                const errorMessage = meta.error || meta.submitError;

                if (variant === "chips") {
                    return (
                        <FormChipGroup
                            error={error}
                            errorMessage={errorMessage}
                            labelProps={labelProps}
                            fieldGroupProps={fieldGroupProps}
                            inputProps={Object.assign({}, inputProps, input, {
                                options,
                            })}
                        />
                    );
                }

                return (
                    <FormSelect
                        error={error}
                        errorMessage={errorMessage}
                        labelProps={labelProps}
                        fieldGroupProps={fieldGroupProps}
                        fieldControlProps={fieldControlProps}
                        adornmentStart={adornmentStart}
                        adornmentEnd={adornmentEnd}
                        inputProps={Object.assign({}, inputProps, input, {
                            options: options,
                            length: !inputProps.multiple ? 1 : inputProps.length ?? undefined,
                        })}
                    />
                );
            }}
        </Field>
    );
};

export default ReferenceField;
